/* ==========================================================================
   Nach-oben-Knopf — Custom Element <fu-nach-oben>
   Heute nur in sections/fu-kategorie-raster.liquid ("Kategorie ·
   Produktliste"), Aussehen in assets/fu-kategorie-raster.css. Plan:
   Dokumentation/Plaene/Plan Kategorieseite - Phase B.md, Feature 8.

   Warum ein eigenes Modul und nicht Teil von assets/fu-filter.js: Suche
   oder Blog sollen den Knopf spaeter ohne das Filterskript nutzen koennen.
   Erwartetes Markup:

     <fu-nach-oben data-fokusziel="h1, #MainContent" hidden>
       <button type="button" aria-label="...">...</button>
     </fu-nach-oben>

   Verhalten:
   - Ohne Skript bleibt das Element hidden (Sektionen-Regel A14). Das Skript
     nimmt hidden weg und schaltet nur data-sichtbar; unsichtbar ist der
     Knopf per visibility auch nicht fokussierbar.
   - Sichtbar ab 3 Bildschirmhoehen (Timo, 30.09.2026). Gemessen wird ueber
     getScrollTop() aus @theme/scroll-container: Ab 990 px scrollt Horizons
     .page-wrapper, window.scrollY ist dort immer 0. Ein scroll-Listener mit
     capture auf document hoert beide Scroller, gedrosselt per rAF.
   - Blendet sich aus, solange er ein Element mit data-fu-nach-oben-frei
     ueberdecken wuerde (die Blaetterleiste; P8) — ausser er hat selbst den
     Fokus. Solange er sichtbar ist, traegt html data-fu-nach-oben; daran
     haengt ein scroll-padding-bottom, damit per Tab angesprungene Elemente
     nicht unter ihm landen (WCAG 2.4.11, G6).
   - Klick: zuerst der Fokus auf das erste Element aus data-fokusziel
     (tabindex="-1" setzt erst das Skript, der Kopf bleibt im HTML
     unveraendert, G11), dann nach oben — weich, bei reduzierter Bewegung
     sofort. 'instant' statt 'auto', weil Horizon scroll-behavior: smooth
     setzt und 'auto' dann trotzdem weich rollte.

   Abhaengigkeit (lautlos, in Dokumentation/Bausteine/Horizon-Aenderungen.md):
   @theme/scroll-container (assets/scroll-container.js) mit getScrollTop und
   scrollTo.
   ========================================================================== */

import { getScrollTop, scrollTo } from '@theme/scroll-container';

const WENIG_BEWEGUNG = window.matchMedia('(prefers-reduced-motion: reduce)');
const BILDSCHIRME = 3;

class FuNachOben extends HTMLElement {
  connectedCallback() {
    this.knopf = this.querySelector('button');
    if (!this.knopf) return;
    this.sichtbar = false;
    this.rahmen = 0;
    this.removeAttribute('hidden');

    document.addEventListener('scroll', this.planen, { capture: true, passive: true });
    window.addEventListener('resize', this.planen, { passive: true });
    window.addEventListener('pageshow', this.planen);
    this.knopf.addEventListener('click', this.beiKlick);
    this.knopf.addEventListener('blur', this.planen);
    this.pruefen();
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.planen, { capture: true });
    window.removeEventListener('resize', this.planen);
    window.removeEventListener('pageshow', this.planen);
    if (this.rahmen) window.cancelAnimationFrame(this.rahmen);
    document.documentElement.removeAttribute('data-fu-nach-oben');
  }

  planen = () => {
    if (this.rahmen) return;
    this.rahmen = window.requestAnimationFrame(() => {
      this.rahmen = 0;
      this.pruefen();
    });
  };

  pruefen() {
    const weit = getScrollTop() > BILDSCHIRME * window.innerHeight;
    const fokus = document.activeElement === this.knopf;
    let zeigen = weit;
    if (zeigen && !fokus && this.verdeckt()) zeigen = false;
    // Wer den Knopf per Tastatur erreicht hat, verliert ihn nicht, nur weil
    // die Schwelle knapp unterschritten wird — der Fokus fiele sonst auf body.
    if (!zeigen && fokus && this.sichtbar) zeigen = true;
    if (zeigen === this.sichtbar) return;
    this.sichtbar = zeigen;
    this.toggleAttribute('data-sichtbar', zeigen);
    document.documentElement.toggleAttribute('data-fu-nach-oben', zeigen);
  }

  /** Wuerde der Knopf ein Element mit data-fu-nach-oben-frei ueberdecken? */
  verdeckt() {
    const r = this.knopf.getBoundingClientRect();
    for (const frei of document.querySelectorAll('[data-fu-nach-oben-frei]')) {
      const f = frei.getBoundingClientRect();
      if (!f.width || !f.height) continue;
      if (r.left < f.right && r.right > f.left && r.top < f.bottom && r.bottom > f.top) return true;
    }
    return false;
  }

  fokusziel() {
    const liste = (this.getAttribute('data-fokusziel') || '#MainContent').split(',');
    for (const auswahl of liste) {
      const s = auswahl.trim();
      if (!s) continue;
      let el = null;
      try {
        el = document.querySelector(s);
      } catch (e) {
        el = null;
      }
      if (el instanceof HTMLElement && el.getClientRects().length) return el;
    }
    return null;
  }

  beiKlick = () => {
    const ziel = this.fokusziel();
    if (ziel) {
      if (!ziel.hasAttribute('tabindex')) ziel.setAttribute('tabindex', '-1');
      ziel.focus({ preventScroll: true });
    }
    scrollTo({ top: 0, behavior: WENIG_BEWEGUNG.matches ? 'instant' : 'smooth' });
  };
}

if (!customElements.get('fu-nach-oben')) {
  customElements.define('fu-nach-oben', FuNachOben);
}
